// Track 3 — Per-session review dashboard.
// Loads /sessions/:id/detail + /sessions/:id/context in parallel and renders
// an extensive single-page review: hero stats, CdA timeline, position
// breakdown, per-region drag attribution, yaw response, AI report block,
// cross-session deltas, sensor highlights, and a CSV export.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from './components/PageLayout';
import Stream from './components/Stream';
import { colors } from './theme';
import PeaksPanel from './components/PeaksPanel';
import { useSubscription } from './services/useSubscription';
import {
  refAeroDistanceLabel,
  refAeroSpeedLabel,
  useUnitSystem,
} from './services/useUnitSystem';
import PaywallModal from './components/PaywallModal';
import DropLinkDialog from './components/DropLinkDialog';
import EditableSessionCopy from './components/EditableSessionCopy';

type TimeSeriesPoint = {
  t: number;
  cda?: number;
  drag_power_w?: number;
  position?: string;
  power_w?: number;
  hr_bpm?: number;
  cadence_rpm?: number;
};

type SessionDetailPayload = {
  id: string;
  source: string;
  startedAt: string;
  endedAt: string | null;
  title?: string | null;
  description?: string | null;
  baselineCda: number | null;
  bestCda: number | null;
  bestPosition: string | null;
  bestSegments: {
    head_pct?: number;
    torso_pct?: number;
    arms_pct?: number;
    legs_pct?: number;
    head_w?: number;
    torso_w?: number;
    arms_w?: number;
    legs_w?: number;
  };
  positions: Array<{
    class: string;
    cda?: number;
    cda_best?: number;
    cda_mean?: number;
    duration_s?: number;
    held_sec?: number;
    held_at?: string;
  }>;
  wattsSavedAt40kph: number | null;
  predictedTtGainW: number | null;
  sensorHighlights: {
    max_power_w?: number;
    avg_power_w?: number;
    avg_hr_bpm?: number;
    max_hr_bpm?: number;
  };
  aiReport: any | null;
  timeSeries: TimeSeriesPoint[];
  // Optional yaw coefficients — some sessions store them in aiReport.context
  // or as top-level extras. We read both.
  yawA?: number;
  yawB?: number;
  yawC?: number;
};

type SessionContextPayload = {
  thisBestCda: number | null;
  bestPosition: string | null;
  allTimeBestCda: number | null;
  allTimeBestPosition: string | null;
  lastVisitBestCda: number | null;
  bestByPosition: Record<string, number>;
  wattsDeltaVsAllTimeAt40kph: number | null;
  wattsDeltaVsLastVisitAt40kph: number | null;
};

// Stable, recognizable colors per canonical position class. Anything we
// haven't enumerated falls back to a neutral chip color.
const POSITION_COLORS: Record<string, string> = {
  drops: '#6affff',
  hoods: '#6aff45',
  tops: '#ffd166',
  aero_tuck: '#ff7ad9',
  aero_bars: '#ff6a45',
  tt: '#a18dff',
  standing: '#ff9a3c',
  sprint: '#ff5a5f',
};

function positionColor(cls: string): string {
  return POSITION_COLORS[cls] ?? colors.textMuted;
}

// Helpers ---------------------------------------------------------------

function formatDuration(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return '—';
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  const sec = Math.max(0, Math.round((end - start) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatHeldSec(sec: number | undefined): string {
  if (!sec || sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

// Apply the standard CdA→watts conversion at 40 km/h.
const WATTS_PER_CDA_AT_40 = 840.45;
function wattsAt40(deltaCda: number | null | undefined): number | null {
  if (deltaCda == null || !isFinite(deltaCda)) return null;
  return Math.round(deltaCda * WATTS_PER_CDA_AT_40 * 10) / 10;
}

// Generate the CSV string from the loaded payload.
function buildCsv(detail: SessionDetailPayload): string {
  const headerBlock = [
    `# aero.chat session ${detail.id}`,
    `# started_at,${detail.startedAt}`,
    `# ended_at,${detail.endedAt ?? ''}`,
    `# best_cda,${detail.bestCda ?? ''}`,
    `# best_position,${detail.bestPosition ?? ''}`,
    `# watts_saved_at_40kph,${detail.wattsSavedAt40kph ?? ''}`,
  ].join('\n');
  const ts = detail.timeSeries ?? [];
  const cols = ['t', 'cda', 'drag_power_w', 'position', 'power_w', 'hr_bpm', 'cadence_rpm'];
  const lines = [cols.join(',')];
  for (const p of ts) {
    lines.push(
      [
        p.t,
        p.cda ?? '',
        p.drag_power_w ?? '',
        p.position ?? '',
        p.power_w ?? '',
        p.hr_bpm ?? '',
        p.cadence_rpm ?? '',
      ].join(','),
    );
  }
  return `${headerBlock}\n\n${lines.join('\n')}\n`;
}

function downloadCsv(name: string, contents: string) {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Small inline yaw parabola SVG using session yaw coefficients
// f(yaw) = a*yaw^2 + b*yaw + c. yaw in degrees.
// Wave 13 / Track G — when `ensemble` is provided, overlay the rider's
// cross-session yaw curve behind the current session's curve so the rider
// can see whether this session is on/off their typical response.
function YawCurve(props: {
  a: number;
  b: number;
  c: number;
  ensemble?: { a: number; b: number; c: number; samples: number } | null;
  ensembleLabel?: string;
}) {
  const { a, b, c, ensemble, ensembleLabel } = props;
  const W = 600;
  const H = 200;
  const margin = { top: 18, right: 16, bottom: 28, left: 36 };
  const xMin = -25;
  const xMax = 25;
  const sample = (coeffs: { a: number; b: number; c: number }) => {
    const out: Array<{ x: number; y: number }> = [];
    for (let x = xMin; x <= xMax; x += 0.5) {
      out.push({ x, y: coeffs.a * x * x + coeffs.b * x + coeffs.c });
    }
    return out;
  };
  const samples = sample({ a, b, c });
  const ensembleSamples = ensemble ? sample(ensemble) : null;
  // Build the y domain over both curves so neither gets clipped.
  const allYs = [
    ...samples.map((s) => s.y),
    ...(ensembleSamples?.map((s) => s.y) ?? []),
  ];
  const yMin = Math.min(...allYs);
  const yMax = Math.max(...allYs);
  const pad = Math.max((yMax - yMin) * 0.15, 0.005);
  const yDomain: [number, number] = [yMin - pad, yMax + pad];
  const xScale = (x: number) =>
    margin.left +
    ((x - xMin) / (xMax - xMin)) * (W - margin.left - margin.right);
  const yScale = (y: number) =>
    margin.top +
    ((yDomain[1] - y) / (yDomain[1] - yDomain[0])) *
      (H - margin.top - margin.bottom);
  const pathFor = (pts: Array<{ x: number; y: number }>) =>
    pts
      .map((s, i) => `${i === 0 ? 'M' : 'L'}${xScale(s.x).toFixed(1)},${yScale(s.y).toFixed(1)}`)
      .join(' ');
  const path = pathFor(samples);
  const ensemblePath = ensembleSamples ? pathFor(ensembleSamples) : null;
  const at10 = a * 10 * 10 + b * 10 + c;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <line
        x1={xScale(0)}
        x2={xScale(0)}
        y1={margin.top}
        y2={H - margin.bottom}
        stroke={colors.border}
        strokeDasharray="2,3"
      />
      <line
        x1={margin.left}
        x2={W - margin.right}
        y1={H - margin.bottom}
        y2={H - margin.bottom}
        stroke={colors.border}
      />
      {/* Track G — ensemble curve drawn first so the session curve sits on top. */}
      {ensemblePath && (
        <>
          <path
            d={ensemblePath}
            fill="none"
            stroke={colors.brand}
            strokeWidth={1.5}
            strokeDasharray="3,3"
            opacity={0.45}
          />
          {ensembleLabel && (
            <text
              x={W - margin.right}
              y={margin.top + 12}
              textAnchor="end"
              fill={colors.textMuted}
              fontFamily='"JetBrains Mono", monospace'
              fontSize={10}
            >
              {ensembleLabel}
            </text>
          )}
        </>
      )}
      <path d={path} fill="none" stroke={colors.brand} strokeWidth={2} />
      <circle cx={xScale(10)} cy={yScale(at10)} r={4} fill={colors.brand} />
      <text
        x={xScale(10) + 8}
        y={yScale(at10) - 8}
        fill={colors.textMuted}
        fontFamily='"JetBrains Mono", monospace'
        fontSize={11}
      >
        10° · {at10.toFixed(3)}
      </text>
      <text
        x={margin.left}
        y={H - 8}
        fill={colors.textFaint}
        fontFamily='"JetBrains Mono", monospace'
        fontSize={10}
      >
        -25°
      </text>
      <text
        x={W - margin.right}
        y={H - 8}
        textAnchor="end"
        fill={colors.textFaint}
        fontFamily='"JetBrains Mono", monospace'
        fontSize={10}
      >
        +25°
      </text>
    </svg>
  );
}

// Per-region horizontal bar chart for the best-frame drag breakdown.
function RegionBars({ segments }: { segments: SessionDetailPayload['bestSegments'] }) {
  const items = [
    { key: 'head', w: segments.head_w, pct: segments.head_pct },
    { key: 'torso', w: segments.torso_w, pct: segments.torso_pct },
    { key: 'arms', w: segments.arms_w, pct: segments.arms_pct },
    { key: 'legs', w: segments.legs_w, pct: segments.legs_pct },
  ];
  const maxW = Math.max(0.001, ...items.map((i) => i.w ?? 0));
  const { t } = useTranslation('session-detail');
  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      {items.map((it) => {
        const w = it.w ?? 0;
        const pct = it.pct ?? 0;
        return (
          <Box key={it.key}>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mb: 0.5 }}
            >
              <Typography sx={{ color: colors.textMuted, fontSize: '0.8125rem' }}>
                {t(`regions.${it.key}`)}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.8125rem',
                  color: colors.text,
                }}
              >
                {w.toFixed(1)} W · {(pct * 100).toFixed(0)}%
              </Typography>
            </Stack>
            <Box
              sx={{
                height: 8,
                width: '100%',
                borderRadius: 4,
                backgroundColor: colors.surfaceElevated,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.max(2, (w / maxW) * 100)}%`,
                  backgroundColor: colors.brand,
                  borderRadius: 4,
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function SectionCard(props: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          {props.title}
        </Typography>
        {props.subtitle && (
          <Typography sx={{ fontSize: '0.8125rem', color: colors.textFaint }}>
            {props.subtitle}
          </Typography>
        )}
      </Stack>
      {props.children}
    </Box>
  );
}

// Main component --------------------------------------------------------

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('session-detail');
  const { hasFullAccess } = useSubscription();
  const units = useUnitSystem();
  const refSpeed = refAeroSpeedLabel(units);
  const refDist = refAeroDistanceLabel(units);

  const [detail, setDetail] = useState<SessionDetailPayload | null>(null);
  const [context, setContext] = useState<SessionContextPayload | null>(null);
  const [yawEnsemble, setYawEnsemble] = useState<
    Record<string, { a: number; b: number; c: number; samples: number; lastUpdatedAt: string }>
  >({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [dropDialogOpen, setDropDialogOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem(
      process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
    );
    if (!token) {
      navigate('/login');
      return;
    }
    const base = process.env.REACT_APP_API || '';
    const headers = { Authorization: `Bearer ${token}` };
    let cancelled = false;
    // Wave 16 — any 402 from a locked endpoint pops the paywall modal
    // instead of leaving the page blank. The detail call is the trigger
    // because /sessions/:id/detail is the primary premium payload.
    const check402 = (r: Response) => {
      if (r.status === 402 && !cancelled) setPaywallOpen(true);
      return r;
    };
    Promise.all([
      fetch(`${base}/sessions/${id}/detail`, { headers })
        .then(check402)
        .then((r) => (r.ok ? r.json() : null)),
      fetch(`${base}/sessions/${id}/context`, { headers })
        .then(check402)
        .then((r) => (r.ok ? r.json() : null)),
      // Track G — rider's persistent yaw model. Failing this call is non-fatal
      // (older accounts or first-yaw-ever sessions just don't get the overlay).
      fetch(`${base}/sessions/me/yaw-ensemble`, { headers })
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({})),
    ])
      .then(([d, c, ye]) => {
        if (cancelled) return;
        if (!d) {
          setNotFound(true);
        } else {
          setDetail(d);
          setContext(c);
          setYawEnsemble(ye || {});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  // Convert the time-series to Stream's {date, value} shape, anchored at
  // the session start so the X axis is real-time and not relative seconds.
  const streamData = useMemo(() => {
    if (!detail) return [];
    const startMs = new Date(detail.startedAt).getTime();
    return detail.timeSeries
      .filter((p) => typeof p.cda === 'number' && p.cda! > 0)
      .map((p) => ({ date: new Date(startMs + p.t * 1000), value: p.cda as number }));
  }, [detail]);

  // Sort positions by best CdA ascending — the strongest position first.
  const sortedPositions = useMemo(() => {
    if (!detail) return [];
    return [...detail.positions].sort((a, b) => {
      const av = a.cda_best ?? a.cda ?? Number.POSITIVE_INFINITY;
      const bv = b.cda_best ?? b.cda ?? Number.POSITIVE_INFINITY;
      return av - bv;
    });
  }, [detail]);

  const yaw = detail?.aiReport?.yaw;
  const yawCoeffs = yaw && typeof yaw.a === 'number' ? yaw : null;

  if (loading) {
    return (
      <PageLayout maxWidth="lg">
        <Typography sx={{ color: colors.textMuted }}>{t('loading')}</Typography>
      </PageLayout>
    );
  }
  if (notFound || !detail) {
    return (
      <PageLayout maxWidth="lg">
        <Stack spacing={2}>
          <Typography sx={{ color: colors.textMuted }}>{t('notFound')}</Typography>
          <Button
            component={RouterLink}
            to="/feed"
            startIcon={<ArrowBackIcon />}
            sx={{ color: colors.brand, alignSelf: 'flex-start' }}
          >
            {t('back')}
          </Button>
        </Stack>
      </PageLayout>
    );
  }

  const durationLabel = formatDuration(detail.startedAt, detail.endedAt);
  const startedAtFormatted = new Date(detail.startedAt).toLocaleString();
  const showFullDetail = hasFullAccess || detail.source === 'rapha';

  return (
    <PageLayout maxWidth="lg">
      <Stack spacing={3}>
        {/* Back link + Drop link + destructive action */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            component={RouterLink}
            to="/feed"
            startIcon={<ArrowBackIcon />}
            sx={{ color: colors.brand, fontSize: '0.8125rem' }}
          >
            {t('back')}
          </Button>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Wave 20.27 — Drop link on indoor aero sessions.
                Opens the unified DropLinkDialog picker. */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDropDialogOpen(true)}
              sx={{
                color: colors.brand,
                borderColor: colors.brand,
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              Drop link
            </Button>
            {/* Wave 20.26 — delete this session. Owner-only enforced
                server-side; endpoint 404s for anyone else. */}
            <Button
              size="small"
              variant="text"
              onClick={async () => {
                if (!id) return;
                if (!window.confirm('Delete this session? This is permanent.')) return;
                try {
                  const jwt = localStorage.getItem(
                    process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
                  );
                  const base = process.env.REACT_APP_API || '';
                  const res = await fetch(`${base}/activities/${id}`, {
                    method: 'DELETE',
                    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
                  });
                  if (!res.ok && res.status !== 204) throw new Error('delete ' + res.status);
                  navigate('/feed');
                } catch (e: any) {
                  // eslint-disable-next-line no-alert
                  alert(e?.message || 'Delete failed');
                }
              }}
              sx={{ color: colors.danger, fontWeight: 700, textTransform: 'none' }}
            >
              Delete
            </Button>
          </Stack>
        </Stack>

        {/* Wave 20.47h — rider-authored title + description. Parity
            with outdoor ActivityDetail + mobile SessionDetailScreen.
            Auto-opens with a server recommendation when Import lands
            us here with ?saved=1. */}
        <EditableSessionCopy
          sessionId={detail.id}
          title={detail.title ?? null}
          description={detail.description ?? null}
          autoOpenIfEmpty={
            typeof window !== 'undefined' &&
            new URLSearchParams(window.location.search).get('saved') === '1'
          }
          onSaved={(next) => {
            setDetail((cur) => (cur ? { ...cur, title: next.title, description: next.description } : cur));
          }}
        />

        {/* Hero */}
        <Box>
          <Typography variant="overline" sx={{ color: colors.brand }}>
            {t('overline')}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
            {startedAtFormatted}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }} alignItems="center">
            <Typography sx={{ color: colors.textMuted, fontSize: '0.875rem' }}>
              {t('duration')}: {durationLabel}
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.875rem' }}>
              {t('source')}: {detail.source}
            </Typography>
            {detail.bestPosition && (
              <Chip
                label={detail.bestPosition}
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${positionColor(detail.bestPosition)}`,
                  color: positionColor(detail.bestPosition),
                  fontSize: '0.6875rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              />
            )}
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() =>
                downloadCsv(
                  t('export.filename', { id: detail.id }),
                  buildCsv(detail),
                )
              }
              sx={{
                ml: 'auto',
                color: colors.brand,
                border: `1px solid ${colors.borderFocus}`,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
              }}
            >
              {t('export.csv')}
            </Button>
          </Stack>
        </Box>

        {/* Hero stats */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.borderFocus}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                {t('hero.bestCda')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: colors.brand,
                  lineHeight: 1.05,
                  mt: 1,
                }}
              >
                {detail.bestCda != null ? detail.bestCda.toFixed(3) : '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                {t('hero.wattsSaved')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: colors.text,
                  lineHeight: 1.05,
                  mt: 1,
                }}
              >
                {detail.wattsSavedAt40kph != null
                  ? `${detail.wattsSavedAt40kph.toFixed(1)} W`
                  : '—'}
              </Typography>
              <Typography sx={{ color: colors.textFaint, fontSize: '0.75rem', mt: 0.5 }}>
                {t('hero.wattsSavedHelp', { refSpeed })}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                {t('hero.ttGain', { refDist })}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: colors.text,
                  lineHeight: 1.05,
                  mt: 1,
                }}
              >
                {detail.predictedTtGainW != null
                  ? `${detail.predictedTtGainW.toFixed(1)} W`
                  : '—'}
              </Typography>
              <Typography sx={{ color: colors.textFaint, fontSize: '0.75rem', mt: 0.5 }}>
                {t('hero.ttGainHelp', { refDist })}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* CdA timeline */}
        <SectionCard title={t('timeline.title')} subtitle={t('timeline.subtitle')}>
          {streamData.length >= 2 ? (
            <Box sx={{ height: 240, width: '100%' }}>
              <Stream data={streamData} />
            </Box>
          ) : (
            <Typography sx={{ color: colors.textFaint, fontStyle: 'italic' }}>
              {t('timeline.placeholder')}
            </Typography>
          )}
        </SectionCard>

        {/* Position breakdown */}
        <SectionCard title={t('positions.title')} subtitle={t('positions.subtitle')}>
          {sortedPositions.length === 0 ? (
            <Typography sx={{ color: colors.textFaint, fontStyle: 'italic' }}>
              {t('positions.empty')}
            </Typography>
          ) : (
            <Stack divider={<Box sx={{ height: '1px', backgroundColor: colors.border }} />}>
              <Grid container sx={{ py: 1.25, color: colors.textMuted, fontSize: '0.6875rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>
                <Grid item xs={4}>{t('positions.col.position')}</Grid>
                <Grid item xs={3}>{t('positions.col.bestCda')}</Grid>
                <Grid item xs={2}>{t('positions.col.heldFor')}</Grid>
                <Grid item xs={3}>{t('positions.col.vsBaseline')}</Grid>
              </Grid>
              {sortedPositions.map((p) => {
                const cda = p.cda_best ?? p.cda ?? null;
                const held = p.held_sec ?? p.duration_s;
                const vsBaseline =
                  detail.baselineCda != null && cda != null
                    ? wattsAt40(detail.baselineCda - cda)
                    : null;
                return (
                  <Grid container alignItems="center" key={`${p.class}-${p.held_at ?? ''}`} sx={{ py: 1.25 }}>
                    <Grid item xs={4}>
                      <Chip
                        label={p.class}
                        size="small"
                        sx={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${positionColor(p.class)}`,
                          color: positionColor(p.class),
                          fontSize: '0.6875rem',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                        }}
                      />
                    </Grid>
                    <Grid item xs={3} sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.text }}>
                      {cda != null ? cda.toFixed(3) : '—'}
                    </Grid>
                    <Grid item xs={2} sx={{ color: colors.textMuted, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}>
                      {formatHeldSec(held)}
                    </Grid>
                    <Grid item xs={3} sx={{ fontFamily: '"JetBrains Mono", monospace', color: vsBaseline && vsBaseline > 0 ? colors.brand : colors.textMuted }}>
                      {vsBaseline != null ? `${vsBaseline > 0 ? '+' : ''}${vsBaseline.toFixed(1)} W` : '—'}
                    </Grid>
                  </Grid>
                );
              })}
            </Stack>
          )}
        </SectionCard>

        {/* Pro-gated sections — show the upgrade prompt if not on a full-access plan. */}
        {showFullDetail ? (
          <>
            <SectionCard title={t('regions.title')} subtitle={t('regions.subtitle')}>
              {Object.keys(detail.bestSegments ?? {}).length === 0 ? (
                <Typography sx={{ color: colors.textFaint, fontStyle: 'italic' }}>
                  {t('regions.empty')}
                </Typography>
              ) : (
                <RegionBars segments={detail.bestSegments} />
              )}
            </SectionCard>

            <SectionCard title={t('yaw.title')} subtitle={t('yaw.subtitle')}>
              {yawCoeffs ? (
                <Box>
                  <Box sx={{ height: 220, width: '100%' }}>
                    <YawCurve
                      a={yawCoeffs.a}
                      b={yawCoeffs.b}
                      c={yawCoeffs.c}
                      // Track G — overlay the rider's cross-session curve only
                      // when it's based on more samples than this session
                      // contributed. Avoids overlaying the same curve on itself.
                      ensemble={
                        detail.bestPosition && yawEnsemble[detail.bestPosition]?.samples >= 2
                          ? yawEnsemble[detail.bestPosition]
                          : null
                      }
                      ensembleLabel={
                        detail.bestPosition && yawEnsemble[detail.bestPosition]?.samples >= 2
                          ? t('yaw.typical', {
                              samples: yawEnsemble[detail.bestPosition].samples,
                            })
                          : undefined
                      }
                    />
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ color: colors.textFaint, fontStyle: 'italic' }}>
                  {t('yaw.empty')}
                </Typography>
              )}
            </SectionCard>

            {/* Wave 20.20 — Peaks (aero-efficiency curve + top
                records for indoor aero sessions). Rendered above
                the AI report so it sees the numeric wins before
                the narrative. */}
            {id && <PeaksPanel activityId={id} isAeroSession />}

            <SectionCard title={t('report.title')}>
              {!detail.aiReport ? (
                <Typography sx={{ color: colors.textFaint, fontStyle: 'italic' }}>
                  {t('report.empty')}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {detail.aiReport.headline && (
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text }}>
                      {detail.aiReport.headline}
                    </Typography>
                  )}
                  {detail.aiReport.biggest_win && (
                    <Box>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 0.5 }}>
                        {t('report.biggestWin')}
                      </Typography>
                      <Typography sx={{ color: colors.text }}>
                        {detail.aiReport.biggest_win}
                      </Typography>
                    </Box>
                  )}
                  {Array.isArray(detail.aiReport.position_ranking) && detail.aiReport.position_ranking.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 1 }}>
                        {t('report.positionRanking')}
                      </Typography>
                      <Stack spacing={0.75}>
                        {detail.aiReport.position_ranking.map((r: any, idx: number) => (
                          <Stack direction="row" key={`${r.position}-${idx}`} spacing={2} alignItems="center">
                            <Typography sx={{ color: colors.textMuted, fontFamily: '"JetBrains Mono", monospace', minWidth: 18 }}>
                              {idx + 1}.
                            </Typography>
                            <Chip
                              label={r.position}
                              size="small"
                              sx={{
                                backgroundColor: 'transparent',
                                border: `1px solid ${positionColor(r.position)}`,
                                color: positionColor(r.position),
                                fontSize: '0.6875rem',
                              }}
                            />
                            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.text }}>
                              {typeof r.cda === 'number' ? r.cda.toFixed(3) : ''}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {Array.isArray(detail.aiReport.next_steps) && detail.aiReport.next_steps.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 1 }}>
                        {t('report.nextSteps')}
                      </Typography>
                      <Stack spacing={0.5} component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {detail.aiReport.next_steps.map((step: string, i: number) => (
                          <Typography component="li" key={i} sx={{ color: colors.text, fontSize: '0.9375rem' }}>
                            {step}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {typeof detail.aiReport.predicted_tt_gain_w === 'number' && (
                    <Box>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 0.5 }}>
                        {t('report.predictedGain', { refDist })}
                      </Typography>
                      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.brand, fontSize: '1.25rem' }}>
                        {detail.aiReport.predicted_tt_gain_w.toFixed(1)} W
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </SectionCard>

            <SectionCard title={t('context.title')}>
              {!context || (context.wattsDeltaVsAllTimeAt40kph == null && context.wattsDeltaVsLastVisitAt40kph == null) ? (
                <Typography sx={{ color: colors.textFaint, fontStyle: 'italic' }}>
                  {t('context.noPrior')}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {context.wattsDeltaVsLastVisitAt40kph != null && (
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                        {t('context.vsLastVisit')}
                      </Typography>
                      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', mt: 0.5, color: context.wattsDeltaVsLastVisitAt40kph >= 0 ? colors.brand : colors.danger }}>
                        {context.wattsDeltaVsLastVisitAt40kph >= 0 ? '+' : ''}
                        {context.wattsDeltaVsLastVisitAt40kph.toFixed(1)} W
                      </Typography>
                      <Typography sx={{ color: colors.textFaint, fontSize: '0.75rem' }}>
                        {context.wattsDeltaVsLastVisitAt40kph >= 0 ? t('context.improvement') : t('context.regression')}
                      </Typography>
                    </Grid>
                  )}
                  {context.wattsDeltaVsAllTimeAt40kph != null && (
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                        {t('context.vsAllTime')}
                      </Typography>
                      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', mt: 0.5, color: context.wattsDeltaVsAllTimeAt40kph >= 0 ? colors.brand : colors.danger }}>
                        {context.wattsDeltaVsAllTimeAt40kph >= 0 ? '+' : ''}
                        {context.wattsDeltaVsAllTimeAt40kph.toFixed(1)} W
                      </Typography>
                      <Typography sx={{ color: colors.textFaint, fontSize: '0.75rem' }}>
                        {context.wattsDeltaVsAllTimeAt40kph >= 0 ? t('context.improvement') : t('context.regression')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </SectionCard>
          </>
        ) : (
          <SectionCard title={t('upgrade.title')} subtitle={t('upgrade.body')}>
            <Button
              component={RouterLink}
              to="/subscription"
              variant="contained"
              sx={{
                mt: 1,
                backgroundColor: colors.brand,
                color: colors.bg,
                '&:hover': { backgroundColor: colors.brand },
                fontWeight: 700,
                letterSpacing: '0.05em',
              }}
            >
              {t('upgrade.cta')}
            </Button>
          </SectionCard>
        )}

        {/* Sensor highlights — visible to everyone (basic stats are free). */}
        <SectionCard title={t('sensors.title')}>
          {!detail.sensorHighlights || Object.keys(detail.sensorHighlights).length === 0 ? (
            <Typography sx={{ color: colors.textFaint, fontStyle: 'italic' }}>
              {t('sensors.empty')}
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {[
                { key: 'avgPower', value: detail.sensorHighlights.avg_power_w, unit: 'W' },
                { key: 'maxPower', value: detail.sensorHighlights.max_power_w, unit: 'W' },
                { key: 'avgHr', value: detail.sensorHighlights.avg_hr_bpm, unit: 'bpm' },
                { key: 'maxHr', value: detail.sensorHighlights.max_hr_bpm, unit: 'bpm' },
              ].map((s) => (
                <Grid item xs={6} md={3} key={s.key}>
                  <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
                    {t(`sensors.${s.key}`)}
                  </Typography>
                  <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', mt: 0.5, color: colors.text }}>
                    {s.value != null ? `${Math.round(s.value)} ${s.unit}` : '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </SectionCard>
      </Stack>
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        reason="locked_feature"
        featureName="Session detail"
      />
      {/* Wave 20.27 — Drop link for indoor aero sessions. Uses the
          same unified picker as ActivityDetail. Same endpoint
          (/activities/:id/share) works for any session_summary
          row regardless of type. */}
      <DropLinkDialog
        open={dropDialogOpen}
        onClose={() => setDropDialogOpen(false)}
        onSubmit={async ({ window, visibility }) => {
          if (!id) return;
          const jwt = localStorage.getItem(
            process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
          );
          const base = process.env.REACT_APP_API || '';
          const res = await fetch(`${base}/activities/${id}/share`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            },
            body: JSON.stringify({ window, visibility }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err?.message || `share ${res.status}`);
          }
          const data = await res.json();
          return { url: data.url };
        }}
        headline="Aero session"
      />
    </PageLayout>
  );
}
